import { DEFAULT_CURRENCY, DEFAULT_REGIONAL_FORMAT } from "@repo/shared";
import {
  type ClientListQuery,
  type ClientListResponse,
  type TenantRegionalSettings,
  type TenantRegionalSettingsUpdateRequest,
  type TenantUserListQuery,
  type TenantUsersWithSsoResponse,
} from "@repo/shared/types";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, patch } from "@/lib/api/_client";
import { getProfileFields } from "@/lib/api/custom-fields";
import { toQueryString } from "@/lib/api/query-string";
import type { ClientWithCustomValues } from "@/lib/types/client";

type MeData = {
  id: string;
  email: string;
  name: string;
  tenantId: string;
  tenantSlug: string;
  permissions: string[];
  language: string;
};

export function useMe() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["me"],
    queryFn: () => get<MeData>("/internal/me"),
  });
  return { me: data ?? null, error, isLoading };
}

/** The paging facts every paged list hook hands back next to its rows. */
export type ListPagination = {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
};

const EMPTY_PAGINATION: ListPagination = { page: 1, pageSize: 20, totalCount: 0, totalPages: 1 };

function paginationOf(data: ListPagination): ListPagination {
  return {
    page: data.page,
    pageSize: data.pageSize,
    totalCount: data.totalCount,
    totalPages: data.totalPages,
  };
}

/**
 * QL-136 — one page of clients. Search, view filters, sort and paging are the
 * server's; the previous page stays on screen while the next one loads.
 * Every mutation that touches a client keeps invalidating the `["clients"]`
 * prefix, which covers every page and filter combination.
 */
export function useClients(query: ClientListQuery = {}) {
  const { filters, ...rest } = query;
  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: ["clients", "list", query],
    queryFn: () =>
      get<ClientListResponse>(
        `/internal/clients${toQueryString({
          ...rest,
          // ClientViewFilter[] doesn't fit toQueryString's flat param shape —
          // JSON-encode it, matching how the route (clients.ts) parses it back.
          filters: filters && filters.length > 0 ? JSON.stringify(filters) : undefined,
        })}`,
      ),
    placeholderData: keepPreviousData,
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["clients"] });
  return {
    // The list item is the same row the detail pages type as ClientWithCustomValues.
    clients: (data?.items ?? []) as unknown as ClientWithCustomValues[],
    pagination: data ? paginationOf(data) : EMPTY_PAGINATION,
    error,
    isLoading,
    isFetching,
    mutate,
  };
}

export function useFavouritedClientIds() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", "favourites"],
    queryFn: () => get<string[]>("/internal/clients/favourites"),
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["clients", "favourites"] });
  return { favouritedClientIds: data ?? [], error, isLoading, mutate };
}

// --- Settings hooks (TanStack Query via internal API) ---

export function useCustomFields() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["customFields", "active"],
    queryFn: () => get<any[]>("/internal/custom-fields/active"),
  });
  return { customFields: data ?? [], error, isLoading };
}

export function useAllCustomFields() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["customFields", "all"],
    queryFn: () => get<any[]>("/internal/custom-fields"),
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["customFields"] });
  return { customFields: data ?? [], error, isLoading, mutate };
}

/**
 * Fields for the client profile card (QL-199): "Show in profile" visibility.
 * Distinct from useCustomFields, which serves the list-visible set — a field
 * visible only in the profile never appears in /active.
 */
export function useProfileFields() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["customFields", "profile"],
    queryFn: getProfileFields,
  });
  return { profileFields: data ?? [], error, isLoading };
}

export function useEditFields() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["customFields", "edit"],
    queryFn: () => get<any[]>("/internal/custom-fields/edit"),
  });
  return { editFields: data ?? [], error, isLoading };
}

export function useClientViews() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clientViews"],
    queryFn: () => get<any[]>("/internal/client-views"),
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["clientViews"] });
  return { views: data ?? [], error, isLoading, mutate };
}

/**
 * The tenant-user pickers (owner select, team members, reassign target). A
 * prefix of the active-only key too: invalidate it whenever a user is
 * created, updated, deleted or restored.
 */
export const TENANT_USERS_KEY = ["tenantUsers"] as const;

export function useTenantUsers() {
  const { data, error, isLoading } = useQuery({
    queryKey: TENANT_USERS_KEY,
    queryFn: () =>
      get<Array<{ id: string; name: string }>>("/internal/client-settings/tenant-users"),
  });
  return { tenantUsers: data ?? [], error, isLoading };
}

/** QL-380 — active (not deleted) users only: the owner select and the group-member picker. */
export function useActiveTenantUsers() {
  const { data, error, isLoading } = useQuery({
    queryKey: [...TENANT_USERS_KEY, "active"],
    queryFn: () =>
      get<Array<{ id: string; name: string }>>("/internal/client-settings/tenant-users?active=1"),
  });
  return { tenantUsers: data ?? [], error, isLoading };
}

export function useClientTabs() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clientTabs"],
    queryFn: () => get<any[]>("/internal/client-tabs"),
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["clientTabs"] });
  return { clientTabs: data ?? [], error, isLoading, mutate };
}

export function useSupportedLanguages() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["supportedLanguages"],
    queryFn: () => get<string[]>("/internal/tenant/supported-languages"),
  });
  return { supportedLanguages: data ?? ["en_GB"], error, isLoading };
}

export function useDisplayDates() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["displayDates"],
    queryFn: () => get<string[]>("/internal/client-settings/display-dates"),
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["displayDates"] });
  return { displayDates: data ?? [], error, isLoading, mutate };
}

// --- Tenant hooks ---

type TenantTheme = {
  logoLightUrl: string | null;
  logoDarkUrl: string | null;
  chartColors: string[] | null;
  themePrimaryColor: string | null;
};

export function useRegionalSettings() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["regionalSettings"],
    queryFn: () => get<TenantRegionalSettings>("/internal/tenant/regional"),
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["regionalSettings"] });
  return {
    settings:
      data ??
      ({
        defaultLanguage: "en_GB",
        supportedLanguages: ["en_GB"],
        ...DEFAULT_REGIONAL_FORMAT,
        currency: DEFAULT_CURRENCY,
      } satisfies TenantRegionalSettings),
    error,
    isLoading,
    mutate,
  };
}

export function useUpdateRegionalSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TenantRegionalSettingsUpdateRequest) =>
      patch<TenantRegionalSettings>("/internal/tenant/regional", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["regionalSettings"] });
      queryClient.invalidateQueries({ queryKey: ["app-bootstrap"] });
      // The tenant currency is what a Cash filter reads in — every filtered list is stale.
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["compliance"] });
    },
  });
}

export function useTenantTheme() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["tenantTheme"],
    queryFn: () => get<TenantTheme>("/internal/tenant/theme"),
  });
  const queryClient = useQueryClient();
  const mutate = () => {
    queryClient.invalidateQueries({ queryKey: ["tenantTheme"] });
    queryClient.invalidateQueries({ queryKey: ["app-bootstrap"] });
  };
  return { theme: data ?? null, error, isLoading, mutate };
}

// --- Users hooks ---

/** QL-136 — one page of the caller's tenant users, plus the tenant's SSO facts. */
export function useUsers(query: TenantUserListQuery = {}) {
  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: ["users", "list", query],
    queryFn: () =>
      get<TenantUsersWithSsoResponse>(
        `/internal/users${toQueryString(query satisfies TenantUserListQuery)}`,
      ),
    placeholderData: keepPreviousData,
  });
  const queryClient = useQueryClient();
  // A user create/update/delete/restore also changes the tenant-user pickers.
  const mutate = () => {
    queryClient.invalidateQueries({ queryKey: ["users"] });
    queryClient.invalidateQueries({ queryKey: TENANT_USERS_KEY });
  };
  return {
    users: data?.items ?? [],
    ssoUserIds: data?.ssoUserIds ?? [],
    hasSsoEnabled: data?.hasSsoEnabled ?? false,
    pagination: data ? paginationOf(data) : EMPTY_PAGINATION,
    error,
    isLoading,
    isFetching,
    mutate,
  };
}
